// src/pages/home/home.jsx
/* eslint-disable no-restricted-globals, no-alert */
import React from 'react';
import {differenceInDays, format, isTomorrow, parseISO} from 'date-fns';
import {ru} from 'date-fns/locale/ru';
import {useBookings} from '../../hooks/useBookings';
import {Loader2} from 'lucide-react';
import axios from 'axios';
import {DOMEN_BASE} from '../../assets/config/constants';
import BookingCard from '../../components/BookingCard';
import ConfirmationModal from '../../components/ConfirmationModal';
import confetti from 'canvas-confetti';
// import sound from '../../sounds/checkout.mp3';

const today = new Date();
const todayStr = format(today, 'yyyy-MM-dd');
// const sound = DOMEN + "/src/sounds/checkout.mp3";

export default function Home() {
    const {bookings, loading, refetch} = useBookings();

    const [modalOpen, setModalOpen] = React.useState(false);
    const [pendingCheckout, setPendingCheckout] = React.useState(null);
    const [leavingId, setLeavingId] = React.useState(null); // для анимации ухода

    const PUBLIC_URL = '/wp-content/include-me/hotel_man_web/';


    // Фильтры
    const todayCheckouts = bookings.filter(b => b.checkOut === todayStr);
    const tomorrowCheckouts = bookings.filter(b => isTomorrow(parseISO(b.checkOut)));
    const soonCheckins = bookings
        .filter(b => {
            const inDate = parseISO(b.checkIn);
            return inDate > today && differenceInDays(inDate, today) <= 5;
        })
        .sort((a, b) => parseISO(a.checkIn) - parseISO(b.checkIn))
        .slice(0, 5);

    const handleCheckout = (id, name) => {
        setPendingCheckout({id, name});
        setModalOpen(true);
    };

    const confirmCheckout = async () => {
        if (!pendingCheckout) return;

        // 1. Запускаем анимацию ухода карточки
        setLeavingId(pendingCheckout.id);

        // 2. Через 400 мс — реально отмечаем в базе + конфетти
        setTimeout(async () => {
            try {
                await axios.post(`${DOMEN_BASE}/api/markCheckout.php`, {id: pendingCheckout.id});

                confetti({
                    particleCount: 130,
                    spread: 80,
                    origin: {y: 0.6},
                    colors: ['#f43f5ed', '#ec4899', '#a855f7', '#3b82f6'],
                });

                // Звук (если __useSound не нужен — просто так)
                const soundPath = PUBLIC_URL + 'static/media/checkout.dc5c87c7c12c12efccd2.mp3';
                const audio = new Audio(soundPath);
                audio.volume = 0.7;
                audio.play().catch(() => {
                });

                setModalOpen(false);
                setPendingCheckout(null);
                setLeavingId(null);
                await refetch(); // обновит список — помеченные исчезнут
            } catch (err) {
                alert('Ошибка при отметке выезда');
                setLeavingId(null);
            }
        }, 400);
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center h-screen">
                <Loader2 className="w-12 h-12 animate-spin text-blue-600"/>
                <p className="mt-4 text-gray-600">Загрузка бронирований...</p>
            </div>
        );
    }

    const hasAny = todayCheckouts.length || tomorrowCheckouts.length || soonCheckins.length;

    return (
        <div className="pt-12 px-4 pb-32 overflow-y-auto min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            <h1 className="text-4xl font-extrabold text-gray-900 mb-8">
                {format(today, 'cccc, d MMMM', {locale: ru})}
            </h1>

            {!hasAny ? (
                <div className="text-center py-20">
                    <div className="text-8xl mb-6">Checkmark</div>
                    <p className="text-2xl font-bold text-green-600">Всё спокойно!</p>
                </div>
            ) : (
                <div className="space-y-12">
                    {/* Сегодня выезд */}
                    {todayCheckouts.length > 0 && (
                        <section>
                            <h2 className="text-2xl font-bold text-red-600 mb-4 flex items-center gap-2">
                                <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></span>
                                Сегодня выезд ({todayCheckouts.length})
                            </h2>
                            <div className="space-y-4">
                                {todayCheckouts.map(b => (
                                    <div
                                        key={b.id}
                                        className={`transition-all duration-400 ease-in-out ${
                                            leavingId === b.id
                                                ? 'translate-x-[-120vw] opacity-0 scale-90'
                                                : 'translate-x-0 opacity-100'
                                        }`}
                                    >
                                        <BookingCard b={b} type="today" onCheckout={handleCheckout}/>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Завтра выезд */}
                    {tomorrowCheckouts.length > 0 && (
                        <section>
                            <h2 className="text-2xl font-bold text-orange-600 mb-4">Завтра выезд</h2>
                            <div className="space-y-4">
                                {tomorrowCheckouts.map(b => (
                                    <div
                                        key={b.id}
                                        className={`transition-all duration-400 ease-in-out ${
                                            leavingId === b.id
                                                ? 'translate-x-[-120vw] opacity-0 scale-90'
                                                : 'translate-x-0 opacity-100'
                                        }`}
                                    >
                                        <BookingCard b={b} type="tomorrow" onCheckout={handleCheckout}/>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Скоро заезд */}
                    {soonCheckins.length > 0 && (
                        <section>
                            <h2 className="text-2xl font-bold text-blue-600 mb-4">Скоро заезд</h2>
                            <div className="space-y-4">
                                {soonCheckins.map(b => (
                                    <BookingCard key={b.id} b={b} type="soon"/>
                                ))}
                            </div>
                        </section>
                    )}
                </div>
            )}

            <ConfirmationModal
                isOpen={modalOpen}
                onConfirm={confirmCheckout}
                onCancel={() => {
                    setModalOpen(false);
                    setPendingCheckout(null);
                    setLeavingId(null); // на всякий случай
                }}
                name={pendingCheckout?.name}
            />
        </div>
    );
}
